"use client"

import { motion, useScroll, useTransform, useMotionValue, useSpring, type Variants } from "framer-motion"
import { PackageCheck } from "lucide-react"
import { useRef, type MouseEvent } from "react"

import { cn } from "@/lib/utils"
import type { StoryMetric } from "./storytelling-types"

/**
 * Interactive visual stage for StoryStageHero.
 *
 * Adds the motion vocabulary the static composition was missing:
 *   - Concentric circles rotate slowly as the section scrolls past viewport
 *   - Product card tilts toward the cursor (3D parallax on mouse position)
 *   - Metric tiles stagger in on viewport entry, lift on hover, with subtle
 *     scroll-linked vertical drift so the composition stays alive even when
 *     the user pauses scrolling
 *   - Backdrop ghost word parallaxes opposite to scroll direction
 *
 * Server component (StoryStageHero) renders text + CTAs; this island owns
 * the right-pane visual stage. Splitting the boundary keeps the headline
 * server-rendered for SEO while still delivering rich motion.
 */

const STAGE_REVEAL: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
}

const TILE_REVEAL: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
}

const TILE_POSITIONS = ["left-2 top-10", "right-2 top-[46%]", "bottom-4 left-10"]

interface StoryStageVisualIslandProps {
  productName: string
  productLabel: string
  metrics: StoryMetric[]
}

export function StoryStageVisualIsland({ productName, productLabel, metrics }: StoryStageVisualIslandProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // Scroll-linked transforms — rotate the rings as the section moves through
  // the viewport. Range is wide so the rotation is felt but never spins fast.
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start end", "end start"] })
  const ringOuterRotate = useTransform(scrollYProgress, [0, 1], [0, 35])
  const ringInnerRotate = useTransform(scrollYProgress, [0, 1], [0, -60])
  const cardDrift = useTransform(scrollYProgress, [0, 1], [12, -12])

  // Mouse-position 3D tilt on the product card. Springs smooth the cursor
  // motion so the tilt has weight (avoids the jittery raw-mouse feeling).
  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const springConfig = { stiffness: 180, damping: 18, mass: 0.4 }
  const rotateX = useSpring(tiltX, springConfig)
  const rotateY = useSpring(tiltY, springConfig)

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    tiltY.set(x * 12) // rotateY range ±6deg
    tiltX.set(-y * 12) // rotateX range ±6deg
  }
  function handleMouseLeave() {
    tiltX.set(0)
    tiltY.set(0)
  }

  return (
    <div ref={containerRef} className="relative z-10 min-h-[28rem] lg:min-h-[31rem]" style={{ perspective: 1200 }}>
      {/* Concentric guide rings — scroll-linked rotation */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 rounded-full border border-primary/12"
        style={{ rotate: ringOuterRotate }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute inset-12 rounded-full border border-primary/18 border-dashed"
        style={{ rotate: ringInnerRotate }}
      />

      {/* Center product card — reveals on viewport entry, tilts on mouse,
          drifts vertically with scroll */}
      <motion.div
        ref={cardRef}
        variants={STAGE_REVEAL}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ y: cardDrift, rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="absolute left-1/2 top-1/2 z-10 grid aspect-[0.78] w-[18rem] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-panel border border-border bg-card p-5 shadow-[var(--shadow-elegant)] transition-shadow hover:shadow-elegant lg:w-[20rem]"
      >
        <div className="flex h-full w-full flex-col justify-between rounded-card border border-border/80 bg-muted p-5" style={{ transform: "translateZ(20px)" }}>
          <div className="flex items-center justify-between">
            <span className="font-heading text-xl font-black uppercase text-primary">{productName}</span>
            <PackageCheck className="size-6 text-primary" />
          </div>
          <div className="grid gap-3">
            {Array.from({ length: 7 }).map((_, index) => (
              <motion.span
                key={index}
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="h-2 origin-left rounded-full bg-primary/20"
              />
            ))}
          </div>
          <div className="rounded-card border border-border bg-card p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">{productLabel}</p>
            <p className="mt-2 font-heading text-4xl font-black text-primary">{metrics[0]?.value ?? "01"}</p>
          </div>
        </div>
      </motion.div>

      {/* Floating metric tiles — staggered viewport reveal + lift on hover */}
      {metrics.slice(0, 3).map((metric, index) => (
        <motion.div
          key={metric.label}
          custom={index}
          variants={TILE_REVEAL}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
          whileHover={{ y: -6, scale: 1.04, transition: { type: "spring", stiffness: 280, damping: 20 } }}
          className={cn(
            "absolute z-20 w-36 cursor-default rounded-card border border-border bg-card/95 p-4 shadow-sm backdrop-blur sm:w-40",
            TILE_POSITIONS[index],
          )}
        >
          <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">{metric.label}</p>
          <p className="mt-2 font-heading text-3xl font-black text-primary">{metric.value}</p>
        </motion.div>
      ))}
    </div>
  )
}

/**
 * Parallax for the giant ghosted backdrop word. Drifts opposite to scroll
 * direction so the section feels layered.
 */
export function StoryStageBackdropIsland({ word }: { word: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] })
  const drift = useTransform(scrollYProgress, [0, 1], [60, -60])
  const fade = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0])

  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      style={{ y: drift, opacity: fade }}
      className="absolute inset-x-0 top-6 select-none text-center font-heading text-[5rem] font-black uppercase leading-none tracking-normal text-primary/[0.045] sm:text-[8rem] lg:text-[11rem]"
    >
      {word}
    </motion.div>
  )
}

/**
 * Magnetic CTA button wrapper — moves slightly toward cursor for a
 * "responsive to attention" feel. Tasteful (8px max pull, springy return).
 */
export function StoryStageMagneticWrapper({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springConfig = { stiffness: 200, damping: 18, mass: 0.3 }
  const sx = useSpring(x, springConfig)
  const sy = useSpring(y, springConfig)

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const relX = (e.clientX - rect.left) / rect.width - 0.5
    const relY = (e.clientY - rect.top) / rect.height - 0.5
    x.set(relX * 16)
    y.set(relY * 12)
  }
  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{ x: sx, y: sy }} className={className}>
      {children}
    </motion.div>
  )
}
